<template>
  <div class="running-workflow-stats">
    <div class="stats-header" v-if="showTitle">
      <h4>{{ title }}</h4>
      <div class="refresh-indicator" v-if="loading">
        <div class="spinner-small"></div>
        <span>Refreshing...</span>
      </div>
    </div>

    <div class="summary-cards">
      <div class="summary-card">
        <div class="card-icon running">
          <i class="bi bi-play-circle-fill"></i>
        </div>
        <div class="card-details">
          <div class="card-label">Currently Running</div>
          <div class="card-value">{{ runningCount }}</div>
        </div>
      </div>
      
      <div class="summary-card paused">
        <div class="card-icon">
          <i class="bi bi-pause-circle-fill"></i>
        </div>
        <div class="card-details">
          <div class="card-label">Paused</div>
          <div class="card-value">{{ pausedCount }}</div>
        </div>
      </div>
      
      <div class="summary-card">
        <div class="card-icon">
          <i class="bi bi-stopwatch-fill"></i>
        </div>
        <div class="card-details">
          <div class="card-label">Longest Running</div>
          <div class="card-value">{{ longestDuration }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import WorkflowHistory from '@/lib/WorkflowHistory';
import { formatDurationShort } from '@/shared/workflowHistoryUtils';

export default {
  name: 'RunningWorkflowStats',
  props: {
    title: {
      type: String,
      default: 'Running Workflows'
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    autoRefresh: {
      type: Boolean,
      default: false
    },
    refreshInterval: {
      type: Number,
      default: 10000 // 10 seconds
    }
  },
  data() {
    return {
      runningWorkflows: [],
      loading: false,
      workflowHistoryService: null,
      refreshTimer: null
    };
  },
  computed: {
    runningCount() {
      return this.runningWorkflows.filter(w => w.status === 'running').length;
    },
    pausedCount() {
      return this.runningWorkflows.filter(w => w.status === 'paused').length;
    },
    longestDuration() {
      if (this.runningWorkflows.length === 0) return '0s';
      const longest = Math.max(
        ...this.runningWorkflows.map(w => this.calculateRunningDuration(w.start_time))
      );
      return formatDurationShort(longest);
    }
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const workflows = await this.workflowHistoryService.fetchRunningWorkflows({});
        this.runningWorkflows = workflows;
      } catch (error) {
        console.error('Error loading running workflows:', error);
        this.runningWorkflows = [];
      } finally {
        this.loading = false;
      }
    },
    
    calculateRunningDuration(startTime) {
      const start = new Date(startTime);
      const now = new Date();
      return Math.floor((now - start) / 1000); // duration in seconds
    },

    startAutoRefresh() {
      if (this.autoRefresh && this.refreshInterval > 0) {
        this.refreshTimer = setInterval(() => {
          this.loadData();
        }, this.refreshInterval);
      }
    },

    stopAutoRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }
    }
  },
  mounted() {
    this.workflowHistoryService = new WorkflowHistory(this.$workflowApi);
    this.loadData();
    this.startAutoRefresh();
  },
  beforeDestroy() {
    this.stopAutoRefresh();
  },
  watch: {
    autoRefresh(newVal) {
      this.stopAutoRefresh();
      if (newVal) {
        this.startAutoRefresh();
      }
    },
    refreshInterval() {
      if (this.autoRefresh) {
        this.stopAutoRefresh();
        this.startAutoRefresh();
      }
    }
  }
};
</script>

<style scoped>
.running-workflow-stats {
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 8px;
  padding: 20px;
}

.stats-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.stats-header h4 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

.refresh-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #9ca3af;
}

.spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid var(--border-default);
  border-top-color: var(--primary-start);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.summary-card {
  background: var(--form-bg);
  border: 1px solid var(--border-default);
  border-radius: 8px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: border-color 0.2s ease;
}

.summary-card:hover {
  border-color: var(--border-hover);
}

.summary-card.paused {
  border-left: 3px solid #f59e0b;
}

.card-icon {
  width: 48px;
  height: 48px;
  background: rgba(108, 99, 255, 0.2);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: var(--primary-start);
  flex-shrink: 0;
}

.card-icon.running {
  background: rgba(16, 185, 129, 0.2);
  color: #10b981;
}

.summary-card.paused .card-icon {
  background: rgba(245, 158, 11, 0.2);
  color: #f59e0b;
}

.card-details {
  flex: 1;
}

.card-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #9ca3af;
  font-weight: 600;
  margin-bottom: 4px;
}

.card-value {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
}
</style>
